import { getValidToken } from '../../lib/helpers/contact-access-token.helpers';
import { pluralTerm } from '../../lib/helpers/term.helper';
import { pluralize } from '../../lib/helpers/text.helpers';
import { SportTermLookup, SportTerminology } from '../../lib/types';
import { IIntlMessage } from '../_core/IntlMsg/intl-msg.types';
import {
  AgeGroupAccessWrite,
  AgeGroupReportCardWrite,
  ConfirmedReportCardWrite,
  IContactLink,
  IReportCardActionOptions,
  IReportCardCaptionContext,
  ISendReport,
  ReportCardAccessMessages,
  ReportCardAvailabilityMessages,
  ReportCardStatusFilter,
  ReportCardWrite,
  SendPreviewOutcome,
  SendSkipReason,
} from './report-card-access.types';

export const reportCardStatusLabels = (messages: ReportCardAccessMessages): Record<ReportCardAccessStatus, IIntlMessage> => ({
  NO_DATA: messages.statusNoData,
  NOT_RELEASED: messages.statusNotReleased,
  AVAILABLE: messages.statusAvailable,
  REVOKED: messages.statusRevoked,
  AGE_GROUP_REVOKED: messages.statusAgeGroupRevoked,
});

// How a never-sent report card gets sent, pointing each viewer only at an action they have.
const notReleasedCaption = (messages: ReportCardAccessMessages, { associationOff, canManage }: IReportCardCaptionContext): IIntlMessage => {
  if (associationOff) return messages.captionNotReleasedAssociationOff;
  return canManage ? messages.captionNotReleased : messages.captionNotReleasedReadOnly;
};

// What a status means for the player's contacts; an available report card needs no explanation.
export const reportCardStatusCaptions = (
  messages: ReportCardAccessMessages,
  context: IReportCardCaptionContext,
): Record<ReportCardAccessStatus, IIntlMessage | null> => ({
  NO_DATA: messages.captionNoData,
  NOT_RELEASED: notReleasedCaption(messages, context),
  AVAILABLE: null,
  REVOKED: messages.captionRevoked,
  AGE_GROUP_REVOKED: messages.captionAgeGroupRevoked,
});

export const reportCardFilterLabels = (messages: ReportCardAccessMessages): Record<ReportCardStatusFilter, IIntlMessage> => ({
  AVAILABLE: messages.statusAvailable,
  NOT_RELEASED: messages.statusNotReleased,
  REVOKED: messages.statusRevoked,
  NO_DATA: messages.statusNoData,
});

// The generic failure and the server-refusal copy for each write; both take {name}, the refusal also {reason}.
export const reportCardWriteFailures = (
  messages: ReportCardAccessMessages,
): Record<ReportCardWrite, { failed: IIntlMessage; refused: IIntlMessage }> => ({
  SEND: { failed: messages.errSendFailed, refused: messages.errSendRefused },
  RESEND: { failed: messages.errResendFailed, refused: messages.errResendRefused },
  REVOKE: { failed: messages.errRevokeFailed, refused: messages.errRevokeRefused },
  RE_ENABLE: { failed: messages.errReEnableFailed, refused: messages.errReEnableRefused },
  EXTEND_LINKS: { failed: messages.errExtendLinksFailed, refused: messages.errExtendLinksRefused },
});

export const reportCardConfirmCopy = (
  messages: ReportCardAccessMessages,
): Record<ConfirmedReportCardWrite, { title: IIntlMessage; body: IIntlMessage; button: IIntlMessage; destructive: boolean }> => ({
  SEND: { title: messages.confirmSendTitle, body: messages.confirmSendBody, button: messages.confirmSendButton, destructive: false },
  RESEND: { title: messages.confirmResendTitle, body: messages.confirmResendBody, button: messages.confirmResendButton, destructive: false },
  REVOKE: { title: messages.confirmRevokeTitle, body: messages.confirmRevokeBody, button: messages.confirmRevokeButton, destructive: true },
  RE_ENABLE: { title: messages.confirmReEnableTitle, body: messages.confirmReEnableBody, button: messages.confirmReEnableButton, destructive: false },
});

interface IReportCardActionContext {
  canManage: boolean;
  // REPORTS on the age group: the report card page's data needs it, so View is offered only with it.
  canOpen: boolean;
  hasCopyableLink: boolean;
  hasExpiringLinks: boolean;
}

// A send releases a report card never sent; a resend and a copied link reach only a player who can open theirs.
// A revoke holds a player out of sends, even before their report card exists or is first sent.
export const reportCardActionOptions = (
  access: IReportCardAccess | null | undefined,
  { canManage, canOpen, hasCopyableLink, hasExpiringLinks }: IReportCardActionContext,
): IReportCardActionOptions => {
  const status = access?.status ?? null;
  const hasData = !!access?.hasData;
  const available = status === 'AVAILABLE';
  const released = hasData && status !== 'NOT_RELEASED' && !!access?.releasedAt;
  return {
    view: canOpen && hasData,
    send: canManage && status === 'NOT_RELEASED',
    resend: canManage && available,
    copyLink: available && hasCopyableLink,
    extendLinks: canManage && released && hasExpiringLinks,
    revoke: canManage && !!status && status !== 'REVOKED',
    reEnable: canManage && status === 'REVOKED',
  };
};

// Whether a player's report card menu has anything to offer; without, it isn't shown.
export const hasAnyReportCardAction = (options: IReportCardActionOptions): boolean => Object.values(options).some(Boolean);

// Who a send notifies or notified, worded for the copy: from a preview before it, or its result after.
export const notifiedRecipientValues = (counts: Pick<IReportCardSendCounts, 'notifiedContactCount' | 'emailCount' | 'smsCount'>) => ({
  count: counts.notifiedContactCount,
  contacts: pluralize(counts.notifiedContactCount, 'contact'),
  emailCount: counts.emailCount,
  emails: pluralize(counts.emailCount, 'email'),
  smsCount: counts.smsCount,
  texts: pluralize(counts.smsCount, 'text'),
});

// Who one player's send or resend told, plus the contacts it couldn't email or text.
const playerRecipientValues = (result: IReportCardSendResult) => ({
  ...notifiedRecipientValues(result),
  unreachableCount: result.unreachableContactCount,
  unreachableContacts: pluralize(result.unreachableContactCount, 'contact'),
  were: pluralize(result.unreachableContactCount, 'was', 'were'),
});

// What one player's first send did, from its own result: who was told, or why nobody was. Already sent covers
// a send before this one (skipped) and one that overlapped it.
export const playerSendReport = (messages: ReportCardAccessMessages, result: IReportCardSendResult): ISendReport => {
  if (!result.playerCount) {
    if (result.alreadySentPlayerCount || result.skipped.alreadyReleased) return { message: messages.msgAlreadySent, variant: 'info' };
    if (result.skipped.notConcluded) return { message: messages.msgNotSentNotConcluded, variant: 'warning' };
    return { message: messages.msgNothingSent, variant: 'warning' };
  }
  if (!result.notifiedContactCount) return { message: messages.msgSentNobodyNotified, variant: 'warning' };
  const message = result.unreachableContactCount ? messages.msgSentWithUnreachable : messages.msgSent;
  return { message, variant: 'success', values: playerRecipientValues(result) };
};

// What one player's resend did. With nobody to message it still renews every contact's link.
export const playerResendReport = (messages: ReportCardAccessMessages, result: IReportCardSendResult): ISendReport => {
  if (!result.playerCount) return { message: messages.msgNothingResent, variant: 'warning' };
  if (!result.notifiedContactCount) {
    return { message: result.accessContactCount ? messages.msgResentLinksRenewed : messages.msgNothingResentNoContacts, variant: 'warning' };
  }
  const message = result.unreachableContactCount ? messages.msgResentWithUnreachable : messages.msgResent;
  return { message, variant: 'success', values: playerRecipientValues(result) };
};

// What an age group's send did, from its own result, plus a note for players an overlapping send told first.
export const ageGroupSendReports = (
  messages: ReportCardAvailabilityMessages,
  result: IReportCardSendResult,
  { ageGroup, nouns }: { ageGroup: string; nouns: { one: string; many: string } },
): ISendReport[] => {
  const players = (count: number) => pluralize(count, nouns.one, nouns.many);
  const overlap = result.alreadySentPlayerCount;
  const overlapValues = { ageGroup, count: overlap, players: players(overlap) };
  if (!result.playerCount) {
    return [overlap
      ? { message: messages.msgNothingSentAlreadySent, variant: 'info', values: overlapValues }
      : { message: messages.msgNothingSentNow, variant: 'warning', values: { ageGroup } }];
  }
  const unreachable = result.unreachablePlayerCount;
  const sent: ISendReport = {
    message: unreachable ? messages.msgSentWithUnreachable : messages.msgSent,
    variant: 'success',
    values: {
      ageGroup,
      ...notifiedRecipientValues(result),
      playerCount: result.playerCount,
      players: players(result.playerCount),
      unreachableCount: unreachable,
      unreachablePlayers: players(unreachable),
      have: pluralize(unreachable, 'has', 'have'),
    },
  };
  return overlap ? [sent, { message: messages.msgSomeSentElsewhere, variant: 'info', values: overlapValues }] : [sent];
};

// The contacts a staff member can copy a working report card link for: an email and an unexpired token.
export const contactLinksOf = (contacts: IPlayerContact[], now: Date = new Date()): IContactLink[] =>
  contacts.flatMap(contact => {
    const token = contact.email ? getValidToken(contact, now) : null;
    return token ? [{ contact, token }] : [];
  });

export const includeNonConcludedSourceCaptions = (
  messages: ReportCardAvailabilityMessages,
): Record<CascadingSettingSource, IIntlMessage> => ({
  AGE_GROUP: messages.captionSourceAgeGroup,
  ASSESSMENT: messages.captionSourceAssessment,
  ASSOCIATION: messages.captionSourceAssociation,
  DEFAULT: messages.captionSourceDefault,
});

export const sendSkipReasonLabels = (messages: ReportCardAvailabilityMessages): Record<SendSkipReason, IIntlMessage> => ({
  notConcluded: messages.skippedNotConcluded,
  noData: messages.skippedNoData,
  notReleased: messages.skippedNotReleased,
  revoked: messages.skippedRevoked,
  alreadyReleased: messages.skippedAlreadyReleased,
  ageGroupRevoked: messages.skippedAgeGroupRevoked,
});

export const ageGroupAccessCopy = (messages: ReportCardAvailabilityMessages): Record<AgeGroupAccessWrite, {
  title: IIntlMessage; body: IIntlMessage; button: IIntlMessage; destructive: boolean;
  done: IIntlMessage; failed: IIntlMessage; refused: IIntlMessage;
}> => ({
  REVOKE: {
    title: messages.confirmRevokeTitle,
    body: messages.confirmRevokeBody,
    button: messages.confirmRevokeButton,
    destructive: true,
    done: messages.msgAgeGroupRevoked,
    failed: messages.errRevokeFailed,
    refused: messages.errRevokeRefused,
  },
  RE_ENABLE: {
    title: messages.confirmReEnableTitle,
    body: messages.confirmReEnableBody,
    button: messages.confirmReEnableButton,
    destructive: false,
    done: messages.msgAgeGroupReEnabled,
    failed: messages.errReEnableFailed,
    refused: messages.errReEnableRefused,
  },
});

// The players a revoke would shut out, or a re-enable let back in.
export const ageGroupAccessAffectedCount = (counts: IReportCardAvailabilityCounts, action: AgeGroupAccessWrite): number =>
  (action === 'REVOKE' ? counts.available : counts.ageGroupRevoked);

export const ageGroupWriteKey = (ageGroupId: string, write: AgeGroupReportCardWrite): string => `${ageGroupId}:${write}`;

// A preview that reaches nobody says why, most actionable reason first; a revoked age group trumps them all.
export const sendPreviewOutcome = (
  preview: IReportCardSendPreview,
  ageGroupId: string,
  includeNonConcluded: boolean,
): SendPreviewOutcome => {
  if (preview.revokedAgeGroupIds.includes(ageGroupId)) return 'REVOKED';
  if (preview.playerCount > 0) return 'READY';
  const { skipped } = preview;
  if (!includeNonConcluded && skipped.notConcluded > 0) return 'NOTHING_NOT_CONCLUDED';
  if (skipped.alreadyReleased > 0) return 'NOTHING_ALL_SENT';
  if (skipped.noData > 0 && skipped.revoked === 0) return 'NOTHING_NO_DATA';
  return 'NOTHING';
};

// The athlete noun in both forms, for `pluralize` beside a count.
export const athleteNouns = (termLookup: SportTermLookup): { one: string; many: string } => {
  const one = termLookup[SportTerminology.ATHLETE].toLowerCase();
  return { one, many: pluralTerm(one) };
};
